import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CreditCard,
  History,
  User as UserIcon,
  FileText,
  Bell,
  Globe,
  LogOut,
  ChevronRight,
  ShieldCheck,
  MapPin,
  XCircle,
  LifeBuoy,
  ExternalLink,
  UserRound,
  Palette,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { getPreferredNavigationProvider, setPreferredNavigationProvider } from '@/lib/navigation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { SkillLevelBadge } from '@/components/common/SkillLevelBadge';
import { SupportContactDialog } from '@/components/support/SupportContactDialog';
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher';
import { useAuthStore, useSchool } from '@sportschool/shared-logic';
import { confirmAndLogout } from '@/lib/logout';
import i18n from '../i18n/i18n';

interface StudentMoreMenuDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
}

export function StudentMoreMenuDialog({
  open,
  onOpenChange,
  schoolId,
}: StudentMoreMenuDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: school } = useSchool(schoolId);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const userContext = useAuthStore((s) => s.userContext);
  const activeRole = useAuthStore((s) => s.activeRole);
  const setActiveRole = useAuthStore((s) => s.setActiveRole);

  const currentLang = i18n.language?.startsWith('pl') ? 'pl' : 'en';

  const email = userContext?.user?.email || '';
  const firstName = userContext?.user?.firstName || '';
  const lastName = userContext?.user?.lastName || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || email;

  const initials = useMemo(() => {
    const f = firstName ? firstName.charAt(0) : '';
    const l = lastName ? lastName.charAt(0) : '';
    if (f || l) return (f + l).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return 'U';
  }, [firstName, lastName, email]);

  const toggleLanguage = () => {
    i18n.changeLanguage(currentLang === 'en' ? 'pl' : 'en');
  };

  const [preferredNav, setPreferredNav] = useState(getPreferredNavigationProvider());
  
  useEffect(() => {
    if (open) {
      setPreferredNav(getPreferredNavigationProvider());
    }
  }, [open]);

  const handleNavigate = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const hasIndividualClasses = !!userContext?.activeSchool?.enabledFeatures?.includes('IndividualClasses');
  const hasRacketStringing = !!userContext?.activeSchool?.enabledFeatures?.includes('RacketStringing');

  const menuItems = [
    ...(hasIndividualClasses
      ? [
          {
            title: t('nav.individualClasses'),
            desc: t('nav.individualClassesDesc'),
            icon: UserRound,
            path: `/schools/${schoolId}/individual-classes`,
            isActive: location.pathname.includes('/individual-classes'),
          },
        ]
      : []),
    ...(hasRacketStringing
      ? [
          {
            title: t('nav.racketStringing'),
            desc: t('nav.racketStringingDesc'),
            icon: Wrench,
            path: `/schools/${schoolId}/racket-stringing`,
            isActive: location.pathname.includes('/racket-stringing'),
          },
        ]
      : []),
    {
      title: t('nav.payments', 'Rozliczenia'),
      desc: t('nav.studentPaymentsDesc', 'Saldo, opłaty i abonamenty'),
      icon: CreditCard,
      path: `/schools/${schoolId}/payments`,
      isActive: location.pathname.includes('/payments'),
    },
    {
      title: t('nav.studentEnrollments', 'Historia zapisów'),
      desc: t('nav.studentEnrollmentsDesc', 'Odbyte treningi i historia grup'),
      icon: History,
      path: `/schools/${schoolId}/student/enrollments`,
      isActive: location.pathname.includes('/student/enrollments'),
    },
    {
      title: t('nav.settings', 'Profil i Dzieci'),
      desc: t('nav.studentProfileDesc', 'Twoje dane, rodzina i zgody'),
      icon: UserIcon,
      path: `/schools/${schoolId}/settings`,
      isActive: location.pathname.includes('/settings'),
    },
    {
      title: t('nav.notifications', 'Powiadomienia'),
      desc: t('nav.studentNotificationsDesc', 'Komunikaty i wiadomości'),
      icon: Bell,
      path: `/schools/${schoolId}/notifications`,
      isActive: location.pathname.includes('/notifications'),
    },
    ...(school?.instructionUrl
      ? [
          {
            title: t('nav.instruction', 'Instrukcja'),
            desc: t('nav.instructionDesc', 'Zewnętrzna instrukcja obsługi'),
            icon: FileText,
            path: 'instruction',
            isExternal: true,
            href: school.instructionUrl,
            isActive: false,
          },
        ]
      : []),
    {
      title: t('nav.help', 'Pomoc'),
      desc: t('nav.helpDesc', 'Formularz kontaktu z obsługą'),
      icon: LifeBuoy,
      path: 'help',
      isHelpAction: true,
      isActive: false,
    },
    {
      title: t('nav.regulations', 'Regulamin'),
      desc: t('nav.studentRegulationsDesc', 'Regulamin szkoły i zasady'),
      icon: FileText,
      path: `/schools/${schoolId}/support/regulations`,
      isActive: location.pathname.includes('/regulations'),
    },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeButtonVariant="onDark"
        showCloseButton={true}
        className="max-h-[90vh] p-0 overflow-hidden flex flex-col gap-0 border border-border/80 bg-card rounded-t-3xl sm:rounded-3xl shadow-2xl sm:max-w-md"
      >
        {/* Canonical Gradient Header matching other dialogs */}
        <DialogHeader className="relative bg-gradient-to-br from-[#003554] to-[#017EBA] p-5 sm:p-6 text-white shrink-0 overflow-hidden text-left space-y-0">
          <div className="absolute -top-10 -right-10 w-44 h-44 bg-white/10 rounded-full blur-3xl pointer-events-none" />

          <div className="flex items-center gap-3.5 relative z-10 pr-6">
            <Avatar className="size-12 border-2 border-white/40 shadow-md ring-2 ring-white/20">
              <AvatarFallback className="bg-white text-[#003554] font-black text-sm">
                {initials}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <DialogTitle className="font-extrabold text-white text-base tracking-tight truncate leading-snug">
                {fullName}
              </DialogTitle>
              <DialogDescription className="text-xs text-sky-100/85 truncate mt-0.5">
                {email}
              </DialogDescription>

              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <Badge
                  variant="outline"
                  className="bg-white/15 text-white border-white/25 text-[10px] font-bold py-0.5 px-2 rounded-md shadow-xs"
                >
                  <ShieldCheck className="w-3 h-3 mr-1 text-emerald-300" />
                  {t('users.roles.student', 'Zawodnik')}
                </Badge>

                {userContext?.activeSchool?.skillLevelName && (
                  <SkillLevelBadge
                    levelName={userContext.activeSchool.skillLevelName}
                    size="sm"
                  />
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">

        {/* Navigation List */}
        <div className="p-4 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1">
            {t('nav.studentMoreAccountSection', 'Moje konto i aktywność')}
          </div>

          {menuItems.map((item) => {
            const Icon = item.icon;
            const handleClick = () => {
              if ('isExternal' in item && item.isExternal && 'href' in item && item.href) {
                onOpenChange(false);
                window.open(item.href as string, '_blank', 'noopener,noreferrer');
              } else if ('isHelpAction' in item && item.isHelpAction) {
                onOpenChange(false);
                setHelpDialogOpen(true);
              } else {
                handleNavigate(item.path);
              }
            };
            const isExternal = 'isExternal' in item && item.isExternal;

            return (
              <button
                key={item.path}
                type="button"
                onClick={handleClick}
                className={`w-full flex items-center justify-between p-3 rounded-2xl transition-all text-left cursor-pointer group ${
                  item.isActive
                    ? 'bg-primary/10 border border-primary/20 text-primary'
                    : 'hover:bg-muted/60 active:bg-muted/80 text-foreground border border-transparent'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`size-9 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 ${
                      item.isActive
                        ? 'bg-primary text-primary-foreground shadow-xs'
                        : 'bg-muted/80 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10'
                    }`}
                  >
                    <Icon className="size-4.5" />
                  </div>

                  <div className="min-w-0">
                    <p className={`text-xs font-bold truncate leading-tight ${item.isActive ? 'text-primary' : 'text-foreground'}`}>
                      {item.title}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate leading-snug mt-0.5">
                      {item.desc}
                    </p>
                  </div>
                </div>

                {isExternal ? (
                  <ExternalLink className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5 text-muted-foreground/60" />
                ) : (
                  <ChevronRight
                    className={`size-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${
                      item.isActive ? 'text-primary' : 'text-muted-foreground/60'
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Settings & Actions Footer */}
        <div className="p-4 pt-0 space-y-2">
          <div className="border-t border-border/60 pt-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1 mb-1">
              {t('nav.studentMoreAppSection', 'Aplikacja')}
            </div>

            {/* Language Switcher */}
            <button
              type="button"
              onClick={toggleLanguage}
              className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-muted/60 active:bg-muted/80 transition-all text-left cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-xl bg-muted/80 flex items-center justify-center text-muted-foreground shrink-0">
                  <Globe className="size-4.5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground leading-tight">
                    {t('userMenu.language', 'Język')}
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                    {currentLang === 'pl' ? 'Polski' : 'English'}
                  </p>
                </div>
              </div>

              <Badge
                variant="outline"
                className="bg-primary/10 text-primary border-primary/20 font-black text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-lg"
              >
                {currentLang === 'pl' ? 'PL' : 'EN'}
              </Badge>
            </button>

            {/* Theme Switcher */}
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-xl bg-muted/80 flex items-center justify-center text-muted-foreground shrink-0">
                  <Palette className="size-4.5" />
                </div>
                <p className="text-xs font-bold text-foreground leading-tight">{t('userMenu.theme')}</p>
              </div>
              <ThemeSwitcher />
            </div>

            {/* Navigation Switcher */}
            {preferredNav && (
              <button
                type="button"
                onClick={() => {
                  setPreferredNavigationProvider(null);
                  setPreferredNav(null);
                  toast.info(t('navigation.defaultCleared', 'Wyczyszczono domyślny wybór nawigacji'));
                }}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-muted/60 active:bg-muted/80 transition-all text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="size-9 rounded-xl bg-muted/80 flex items-center justify-center text-muted-foreground shrink-0">
                    <MapPin className="size-4.5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground leading-tight">
                      {t('navigation.defaultApp', 'Domyślna nawigacja')}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                      {preferredNav === 'google' ? 'Google Maps' : preferredNav === 'waze' ? 'Waze' : 'Apple Maps'}
                    </p>
                  </div>
                </div>

                <Badge
                  variant="outline"
                  className="bg-destructive/10 text-destructive border-destructive/20 font-bold text-[10px] uppercase tracking-wider px-2 py-1 rounded-lg flex items-center gap-1"
                >
                  <XCircle className="w-3 h-3" />
                  {t('navigation.clear', 'Wyczyść')}
                </Badge>
              </button>
            )}

            {/* Role Switcher if multi-role */}
            {userContext?.activeSchool?.roles && userContext.activeSchool.roles.length > 1 && (
              <div className="p-2.5 bg-muted/30 rounded-2xl border border-border/50 mt-2 space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">
                  {t('userMenu.changeRole', 'Zmień aktywną rolę')}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {userContext.activeSchool.roles.map((r) => {
                    const isRoleActive = activeRole === r;
                    let label = r;
                    if (r === 'Admin') label = t('users.roles.admin');
                    if (r === 'Coach') label = t('users.roles.coach');
                    if (r === 'Student') label = t('users.roles.student');

                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => {
                          setActiveRole(r);
                          onOpenChange(false);
                          if (r === 'Student') {
                            navigate(`/schools/${schoolId}/student`);
                          } else if (r === 'Coach') {
                            navigate(`/schools/${schoolId}/coach`);
                          } else {
                            navigate(`/schools/${schoolId}/dashboard`);
                          }
                        }}
                        className={`text-xs font-bold py-2 px-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                          isRoleActive
                            ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                            : 'bg-card text-foreground border-border/70 hover:bg-muted/50'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Logout */}
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                void confirmAndLogout();
              }}
              className="w-full flex items-center gap-3 p-3 mt-1.5 rounded-2xl hover:bg-rose-500/10 active:bg-rose-500/20 text-destructive transition-all cursor-pointer"
            >
              <div className="size-9 rounded-xl bg-rose-500/10 flex items-center justify-center text-destructive shrink-0">
                <LogOut className="size-4.5" />
              </div>
              <span className="text-xs font-bold leading-tight">
                {t('nav.logout', 'Wyloguj się')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>

    <SupportContactDialog
      open={helpDialogOpen}
      onOpenChange={setHelpDialogOpen}
      schoolId={schoolId}
    />
  </>
);
}
